/**
 * AuthView - Login UI Component
 * 
 * Clean, white-themed authentication interface for Maestro.
 * Features:
 * - Email/password sign in
 * - Password reset flow
 * - Signup starts a no-card 30-day DB trial (same as website Try Free)
 * - Form validation, loading states, error handling
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { isValidRefreshToken, tokenFpSync } from '../lib/authTokens';
import { ValetIcon } from './icons/ValetIcon';
import { resetRefreshState, ensureFreshAccessToken, isRefreshTokenAlreadyUsedError, refreshAccessTokenShared } from '../services/api';
import {
  clearExplicitSignOut,
  markExplicitSignOut,
  noteAuthedOnceThisInstall,
  shouldDeclawInvoluntaryLogout,
} from '../lib/authDeclaw';
import { interceptInvoluntaryLogout } from '../lib/authDeclawShadow';
import {
  clearTerminalAuthFamilyLatch,
  markUsableAuthedStateReached,
  hasReachedUsableAuthedState,
  isBootTimeTerminalRejection,
  purgeBootTerminalStoredSession,
} from '../lib/authTerminalLatch';
import { latchBootTerminalSignInRequired } from '../lib/authBootTerminalSignIn';
import {
  isSessionDead,
  isPoisonedRefreshToken,
  markSessionDead,
  resetSessionDeadState,
} from '../lib/sessionDead';
import { applyAuthDeclawShadow } from '../lib/authDeclawShadow';
import { resetServiceSessionDeadState } from '../lib/serviceSessionDead';
import {
  classifyRefreshFailure,
  logBootTokenSnapshot,
  logRefreshFailed,
  logRefreshRequested,
  logSignoutDecision,
  parseRefreshErrorDetails,
} from '../lib/authDiagnostics';
import { recoverSessionFromAuthJson } from '../lib/authJsonRestore';
import { useTranslation } from '../i18n/useTranslation';
import CommunityAuthMessaging, { communityLayoutStyles } from './CommunityAuthMessaging';
import { isIosShell } from '../utils/platform';
import {
  clearRememberedLoginEmail,
  getRememberedLoginEmail,
  setRememberedLoginEmail,
} from '../utils/rememberedLoginEmail';
import {
  startAuthInitWatchdog,
  armAuthInitWatchdog,
  registerAuthInitForceTimeout,
  refreshAuthJsonSession,
  setAuthInitStep,
} from '../lib/authInitWatchdog';
import {
  clearBootSurrendered,
  isBootSurrendered,
  markBootSurrendered,
  noteBootAuthProgress,
  registerBootDeadlineStallGate,
} from '../lib/bootDeadline';
import { notifyBatonAuthReady } from '../lib/batonAuthGate';
import { hydrateAuthUserWithRetry } from '../lib/authHydrate';
import {
  clearBootHoldSignIn,
  clearConnectorCredentialsForDeadSession,
  hydrateBootHoldSignInFromSession,
  persistBootHoldSignIn,
  shouldBlockAutomaticSessionRestore,
} from '../lib/authBootHoldSignIn';

let authListeners = [];
let authState = {
  isAuthenticated: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  adminRole: null,
  adminPermissions: null,
  isLoading: true,
  error: null,
  sessionRevoked: false,
  authInitTimedOut: false,
  sessionBootstrapFailed: false,
  bootStillConnecting: false,
  bootConnectivityNote: false,
};

let _refreshAlreadyFailed = false;
let _proactiveRefreshTimer = null;

function clearProactiveRefreshTimer() {
  if (_proactiveRefreshTimer) {
    clearTimeout(_proactiveRefreshTimer);
    _proactiveRefreshTimer = null;
  }
}

/** Refresh ~5 min before JWT expiry so users stay signed in across idle periods. */
function scheduleProactiveRefresh(accessToken) {
  clearProactiveRefreshTimer();
  const expMs = decodeJwtExpMs(accessToken);
  if (!expMs) return;
  const refreshIn = Math.max(expMs - Date.now() - 5 * 60 * 1000, 30_000);
  _proactiveRefreshTimer = setTimeout(() => {
    refreshAuthSession().catch(() => {});
  }, refreshIn);
}

function markRefreshFailed() {
  _refreshAlreadyFailed = true;
  try { sessionStorage.setItem('maestro_refresh_failed', '1'); } catch {}
}

function isRefreshBlocked() {
  if (_refreshAlreadyFailed) return true;
  try { return sessionStorage.getItem('maestro_refresh_failed') === '1'; } catch { return false; }
}

function clearRefreshBlock() {
  _refreshAlreadyFailed = false;
  try { sessionStorage.removeItem('maestro_refresh_failed'); } catch {}
}

export function getAuthState() {
  return authState;
}

/** Prefer localStorage — may be fresher than in-memory authState after api.ts refresh. */
export function getLiveAccessToken() {
  return getStoredAuthRecord()?.accessToken || authState.accessToken || null;
}

/** Declaw dead-family breakthrough — surface login without clearing library/settings. */
export function applyDeadFamilyLoginBreakthrough() {
  setAuthState({
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    user: authState.user,
    adminRole: authState.adminRole,
    adminPermissions: authState.adminPermissions,
    isLoading: false,
    error: null,
    sessionRevoked: true,
    authInitTimedOut: false,
    sessionBootstrapFailed: false,
  });
}
/** Boot-time terminal family rejection — sign-in screen, not declaw playback shell. */
export function applyBootTerminalSignInRequired() {
  _restoreFromAuthJsonAbort = true;
  latchBootTerminalSignInRequired();
  persistBootHoldSignIn('dead_refresh');
  void clearConnectorCredentialsForDeadSession('AuthView:boot_terminal');
  setAuthState({
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    user: authState.user,
    adminRole: authState.adminRole,
    adminPermissions: authState.adminPermissions,
    isLoading: false,
    error: null,
    sessionRevoked: true,
    authInitTimedOut: false,
    sessionBootstrapFailed: false,
  });
}

/** Read maestro_auth from localStorage (may be fresher than in-memory authState). */
export function getStoredAuthRecord() {
  try {
    const raw = localStorage.getItem('maestro_auth');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Sync in-memory auth from localStorage after api.ts token refresh. */
function syncAuthFromLocalStorage() {
  const stored = getStoredAuthRecord();
  if (!stored?.accessToken) return;
  const updates = {};
  if (stored.accessToken && stored.accessToken !== authState.accessToken) {
    updates.accessToken = stored.accessToken;
  }
  if (stored.refreshToken && stored.refreshToken !== authState.refreshToken) {
    updates.refreshToken = stored.refreshToken;
  }
  if (Object.keys(updates).length > 0) {
    setAuthState(updates);
  }
}

function decodeJwtPayload(token) {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return null;
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

function decodeJwtExpMs(token) {
  const payload = decodeJwtPayload(token);
  const expSec = Number(payload?.exp || 0);
  if (!Number.isFinite(expSec) || expSec <= 0) return null;
  return expSec * 1000;
}

/** Best-effort email from in-memory auth, localStorage, or JWT claims. */
export function resolveAuthEmail() {
  const direct = authState.user?.email;
  if (direct) return direct;
  const stored = getStoredAuthRecord()?.user?.email;
  if (stored) return stored;
  const payload = decodeJwtPayload(authState.accessToken || getStoredAuthRecord()?.accessToken);
  return payload?.email || payload?.user_metadata?.email || null;
}

/** Best-effort user id from auth user object, localStorage, or JWT sub claim. */
export function resolveAuthUserId(user, accessToken) {
  const fromArg = String(user?.id || '').trim();
  if (fromArg) return fromArg;
  const stored = String(getStoredAuthRecord()?.user?.id || '').trim();
  if (stored) return stored;
  const token = accessToken || authState.accessToken || getStoredAuthRecord()?.accessToken;
  const payload = decodeJwtPayload(token);
  return String(payload?.sub || '').trim();
}

function applyAuthUserPayload(data) {
  if (!data) return;
  const updates = {};
  if (data.user) updates.user = data.user;
  if (data.adminRole) {
    updates.adminRole = data.adminRole;
    updates.adminPermissions = data.adminPermissions || null;
  }
  if (Object.keys(updates).length) setAuthState(updates);
}

async function runBackgroundAuthHydrate(token, source = 'background') {
  if (!token) return;
  setAuthInitStep('hydrate_user');
  await hydrateAuthUserWithRetry({
    getBearerToken: () => authState.accessToken || getStoredAuthRecord()?.accessToken || token,
    onPayload: applyAuthUserPayload,
    source,
  });
  setAuthInitStep('ready');
}

/** Fetch /api/auth/user and merge user + adminRole into auth state. */
export async function hydrateAuthUserFromApi() {
  const token = authState.accessToken || getStoredAuthRecord()?.accessToken;
  if (!token) return null;
  try {
    const res = await fetch(`${API_URL}/api/auth/user`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    applyAuthUserPayload(data);
    return data;
  } catch {
    return null;
  }
}

export function subscribeToAuth(listener) {
  authListeners.push(listener);
  return () => {
    authListeners = authListeners.filter(l => l !== listener);
  };
}

function notifyAuthListeners() {
  authListeners.forEach(l => l(authState));
}

let _restoreFromAuthJsonAbort = false;

/** Re-export for callers that import from AuthView. */
export { isValidRefreshToken } from '../lib/authTokens';

function pickValidRefreshToken(...candidates) {
  for (const t of candidates) {
    if (isValidRefreshToken(t)) return t;
  }
  return null;
}

function extractSessionTokens(data) {
  const session = data?.session || null;
  const accessToken =
    session?.access_token || session?.accessToken ||
    data?.access_token || data?.accessToken || null;
  const refreshToken = pickValidRefreshToken(
    session?.refresh_token,
    data?.refresh_token,
    session?.refreshToken,
    data?.refreshToken,
  );
  if (!refreshToken) {
    const rejected = session?.refresh_token || data?.refresh_token;
    if (rejected && !isValidRefreshToken(rejected)) {
      console.warn(`[Auth] Ignoring invalid refresh_token (len=${rejected.length})`);
    }
  }
  return { accessToken, refreshToken };
}

function persistAuthToLocalStorage() {
  if (isSessionDead() || isPoisonedRefreshToken(authState.refreshToken)) {
    localStorage.removeItem('maestro_auth');
    return;
  }
  if (!authState.accessToken) {
    localStorage.removeItem('maestro_auth');
    return;
  }
  localStorage.setItem('maestro_auth', JSON.stringify({
    accessToken: authState.accessToken,
    refreshToken: authState.refreshToken || null,
    user: authState.user,
    adminRole: authState.adminRole || null,
    adminPermissions: authState.adminPermissions || null,
  }));
}

async function syncDaemonCredentials(refreshToken, userId, email, { force = false } = {}) {
  if (typeof window === 'undefined' || !('__TAURI__' in window)) return;
  const fp = tokenFpSync(refreshToken || '');
  if (!isValidRefreshToken(refreshToken)) {
    console.warn(`[Auth:lifecycle] syncDaemonCredentials SKIPPED invalid token fp=${fp}`);
    return;
  }
  const source = force ? 'AuthView:sign-in' : 'AuthView:sync';
  console.info(`[Auth:lifecycle] syncDaemonCredentials write-back fp=${fp} force=${force}`);
  try {
    const { writeDaemonCredentials, writeDaemonCredentialsIfNeeded } = await import('../lib/authCredentialSeed');
    const { refreshOnceOnDeadThisBoot } = await import('../lib/authDeadFpLatch');
    const result = force
      ? await writeDaemonCredentials(refreshToken, userId || '', email || '', source, { failHard: true })
      : await writeDaemonCredentialsIfNeeded(refreshToken, userId || '', email || '', source);
    if (await refreshOnceOnDeadThisBoot(force, result, fp, refreshAuthSession)) return;
    console.info(`[Auth:lifecycle] auth.json write-back ${result?.written === false ? 'skipped' : 'complete'} fp=${fp} path=${result.path}`);
  } catch (e) {
    console.warn(`[Auth:lifecycle] auth.json write-back FAILED fp=${fp}`, e);
    if (force) throw e;
  }
}

function adoptCachedSessionFromStorage(source = 'boot-deadline-cache') {
  if (shouldBlockAutomaticSessionRestore()) return false;
  const stored = getStoredAuthRecord();
  if (!stored?.accessToken || !isValidRefreshToken(stored?.refreshToken)) return false;
  markUsableAuthedStateReached();
  clearBootSurrendered();
  setAuthState({
    isAuthenticated: true,
    accessToken: stored.accessToken,
    refreshToken: stored.refreshToken,
    user: stored.user ?? authState.user,
    adminRole: stored.adminRole ?? authState.adminRole,
    adminPermissions: stored.adminPermissions ?? authState.adminPermissions,
    isLoading: false,
    error: null,
    sessionRevoked: false,
    authInitTimedOut: false,
    sessionBootstrapFailed: false,
    bootStillConnecting: false,
    bootConnectivityNote: false,
  });
  scheduleProactiveRefresh(stored.accessToken);
  notifyBatonAuthReady(source);
  void runBackgroundAuthHydrate(stored.accessToken, `${source}:hydrate`);
  return true;
}

function forceAuthInitTimeout() {
  if (hasReachedUsableAuthedState() || isBootSurrendered() || shouldBlockAutomaticSessionRestore()) return;
  if (isBootTimeTerminalRejection()) {
    applyBootTerminalSignInRequired();
    return;
  }
  // Cached credentials — never sign-in wall for slow HTTPS hydrate (#227).
  if (adoptCachedSessionFromStorage('boot-deadline-cache')) return;
  surrenderBootToSignIn({ stillConnecting: true });
}

/** Unconditional route to sign-in — hard deadline or user escape hatch. */
export function surrenderBootToSignIn({ stillConnecting = false, connectivityNote = false } = {}) {
  _restoreFromAuthJsonAbort = true;
  markBootSurrendered(stillConnecting);
  if (!stillConnecting) {
    persistBootHoldSignIn('boot_surrender');
  }
  setAuthInitStep('unauthenticated');
  purgeBootTerminalStoredSession();
  setAuthState({
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    user: null,
    adminRole: null,
    adminPermissions: null,
    isLoading: false,
    error: null,
    sessionRevoked: false,
    authInitTimedOut: stillConnecting,
    sessionBootstrapFailed: stillConnecting || connectivityNote,
    bootStillConnecting: stillConnecting,
    bootConnectivityNote: connectivityNote,
  });
}

registerAuthInitForceTimeout(forceAuthInitTimeout);

registerBootDeadlineStallGate(() => {
  if (hasReachedUsableAuthedState()) return false;
  if (isBootSurrendered()) return false;
  if (isBootTimeTerminalRejection() || isSessionDead()) return true;
  const token = authState.accessToken || getStoredAuthRecord()?.accessToken;
  if (token && isValidRefreshToken(getStoredAuthRecord()?.refreshToken || authState.refreshToken)) {
    const expMs = decodeJwtExpMs(token);
    if (expMs && expMs > Date.now()) return false;
  }
  return !authState.isAuthenticated && authState.isLoading;
});

function setAuthState(updates) {
  authState = { ...authState, ...updates };
  notifyAuthListeners();
  armAuthInitWatchdog(authState.isLoading);

  const persistKeys = ['accessToken', 'refreshToken', 'user', 'adminRole', 'adminPermissions'];
  if (persistKeys.some(k => updates[k] !== undefined)) {
    persistAuthToLocalStorage();
  }
}

export async function refreshAuthSession() {
  const refreshToken = authState.refreshToken || getStoredAuthRecord()?.refreshToken;
  if (!isValidRefreshToken(refreshToken)) {
    throw new Error('No refresh token');
  }
  const oldFp = tokenFpSync(refreshToken);
  console.info(`[Auth:lifecycle] AuthView refreshAuthSession attempt old_fp=${oldFp}`);

  const result = await refreshAccessTokenShared('AuthView:refreshAuthSession');

  if (result.fatalRejected) {
    try {
      logSignoutDecision('fatal_rejected', 'fatal');
    } catch { /* diagnostic only */ }
    markRefreshFailed();
    await signOut();
    throw new Error('Token refresh failed');
  }

  if (!result.accessToken || !isValidRefreshToken(result.refreshToken)) {
    throw new Error('Token refresh failed');
  }

  const newToken = result.accessToken;
  const newRefresh = result.refreshToken;
  const newFp = tokenFpSync(newRefresh);

  console.info(
    `[Auth:lifecycle] AuthView refreshAuthSession success old_fp=${oldFp} new_fp=${newFp} ` +
    `localStorage=write auth.json=pending refreshed=${result.refreshed} absorbed=${result.alreadyUsedAbsorbed}`,
  );
  clearRefreshBlock();
  resetRefreshState();
  syncAuthFromLocalStorage();
  setAuthState({
    isAuthenticated: true,
    accessToken: newToken,
    refreshToken: newRefresh,
    isLoading: false,
    error: null,
  });
  scheduleProactiveRefresh(newToken);

  syncDaemonCredentials(newRefresh, authState.user?.id, authState.user?.email);

  try {
    const userRes = await fetch(`${API_URL}/api/auth/user`, {
      headers: { Authorization: `Bearer ${newToken}` },
    });
    if (userRes.ok) {
      applyAuthUserPayload(await userRes.json());
    }
  } catch {}

  return {
    access_token: newToken,
    refresh_token: newRefresh,
  };
}

const API_URL = 'https://api.maestroaudio.net';

function logSessionRestoreOutcome(source, refreshStatus, accessTokenLen, newRefreshLen) {
  console.info('[Auth] session restore outcome', {
    source,
    refresh_status: refreshStatus,
    access_token_len: accessTokenLen ?? 0,
    new_refresh_len: newRefreshLen ?? 0,
  });
}

function applyRestoredSession({ accessToken, refreshToken, user, source }) {
  if (isSessionDead() || isPoisonedRefreshToken(refreshToken)) return;
  clearRefreshBlock();
  resetRefreshState();
  clearTerminalAuthFamilyLatch();
  clearBootSurrendered();
  clearBootHoldSignIn();
  noteBootAuthProgress();
  markUsableAuthedStateReached();
  setAuthState({
    isAuthenticated: true,
    accessToken,
    refreshToken,
    user,
    isLoading: false,
    error: null,
    sessionRevoked: false,
  });
  persistAuthToLocalStorage();
  scheduleProactiveRefresh(accessToken);
  syncDaemonCredentials(refreshToken, user?.id, user?.email);
  noteAuthedOnceThisInstall();
  notifyBatonAuthReady(`restore:${source}`);
  logSessionRestoreOutcome(source, 'ok', accessToken?.length ?? 0, refreshToken?.length ?? 0);
}

function onSessionExpiredEvent() {
  if (interceptInvoluntaryLogout('session_expired_event', 'maestro:session-expired')) {
    return;
  }
  clearProactiveRefreshTimer();
  _restoreFromAuthJsonAbort = true;
  setAuthState({
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    user: null,
    adminRole: null,
    adminPermissions: null,
    isLoading: false,
    error: null,
    sessionRevoked: true,
  });
}

function onDeclawShellRestore() {
  if (!shouldDeclawInvoluntaryLogout()) return;
  const stored = getStoredAuthRecord();
  if (!stored?.accessToken || !isValidRefreshToken(stored?.refreshToken)) return;
  setAuthState({
    isAuthenticated: true,
    accessToken: stored.accessToken,
    refreshToken: stored.refreshToken,
    user: stored.user ?? authState.user,
    isLoading: false,
    error: null,
    sessionRevoked: false,
  });
}

function onSessionDeadCleared() {
  setAuthState({ sessionRevoked: false });
}

function onTokenRefreshed(ev) {
  syncAuthFromLocalStorage();
  const token = ev?.detail?.accessToken || authState.accessToken;
  if (token) scheduleProactiveRefresh(token);
}

function onAuthVisibilityChange() {
  if (document.visibilityState !== 'visible') return;
  if (isSessionDead()) return;
  const stored = getStoredAuthRecord();
  const token = authState.accessToken || stored?.accessToken;
  const refresh = authState.refreshToken || stored?.refreshToken;
  if (!token || !isValidRefreshToken(refresh)) return;
  const expMs = decodeJwtExpMs(token);
  if (expMs && expMs - Date.now() < 5 * 60 * 1000) {
    refreshAuthSession().catch(() => {});
  }
}

let _authWindowListenerRefCount = 0;
let _authWindowListenersActive = false;

/** Synchronous mount-time registration — never call from async continuations. */
export function registerAuthWindowListeners() {
  if (typeof window === 'undefined') return;
  _authWindowListenerRefCount += 1;
  if (_authWindowListenersActive) return;
  _authWindowListenersActive = true;
  window.addEventListener('maestro:session-expired', onSessionExpiredEvent);
  window.addEventListener('maestro:session-dead-cleared', onSessionDeadCleared);
  window.addEventListener('maestro:token-refreshed', onTokenRefreshed);
  window.addEventListener('maestro:declaw-shell-restore', onDeclawShellRestore);
  document.addEventListener('visibilitychange', onAuthVisibilityChange);
}

export function disposeAuthWindowListeners() {
  if (typeof window === 'undefined') return;
  _authWindowListenerRefCount = Math.max(0, _authWindowListenerRefCount - 1);
  if (_authWindowListenerRefCount > 0 || !_authWindowListenersActive) return;
  _authWindowListenersActive = false;
  window.removeEventListener('maestro:session-expired', onSessionExpiredEvent);
  window.removeEventListener('maestro:session-dead-cleared', onSessionDeadCleared);
  window.removeEventListener('maestro:token-refreshed', onTokenRefreshed);
  window.removeEventListener('maestro:declaw-shell-restore', onDeclawShellRestore);
  document.removeEventListener('visibilitychange', onAuthVisibilityChange);
}

function initializeAuth() {
  hydrateBootHoldSignInFromSession();
  if (shouldBlockAutomaticSessionRestore()) {
    setAuthState({
      isAuthenticated: false,
      accessToken: null,
      refreshToken: null,
      isLoading: false,
      sessionRevoked: isBootTimeTerminalRejection() || isSessionDead(),
      authInitTimedOut: false,
      sessionBootstrapFailed: false,
      bootStillConnecting: false,
    });
    return false;
  }
  if (isBootSurrendered()) {
    setAuthState({ isLoading: false });
    return false;
  }
  _restoreFromAuthJsonAbort = false;
  setAuthInitStep('boot');
  noteBootAuthProgress();

  if (isSessionDead()) {
    setAuthState({
      isAuthenticated: false,
      accessToken: null,
      refreshToken: null,
      user: null,
      adminRole: null,
      adminPermissions: null,
      isLoading: false,
      error: null,
      sessionRevoked: true,
    });
    return false;
  }

  // Fresh app launch: retry restore even if a prior tab set maestro_refresh_failed.
  if (isRefreshBlocked()) {
    clearRefreshBlock();
    resetRefreshState();
    console.log('[Auth] cleared stale refresh-failed flag — retrying session restore');
  }

  try {
    const stored = localStorage.getItem('maestro_auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      let { accessToken, refreshToken, user, adminRole, adminPermissions } = parsed;
      // Guard against corrupt storage (e.g. literal "refreshToken" string — 12 chars).
      if (!isValidRefreshToken(refreshToken)) {
        console.warn(`[Auth] Stored refresh_token invalid (len=${(refreshToken || '').length}) — will try auth.json or re-login`);
        refreshToken = null;
      }

      const needsAuthJsonRestore =
        !isValidRefreshToken(refreshToken)
        && typeof window !== 'undefined'
        && '__TAURI__' in window;

      if (needsAuthJsonRestore) {
        setAuthInitStep('restore_auth_json');
        setAuthState({
          isAuthenticated: false,
          accessToken: null,
          refreshToken: null,
          user: null,
          adminRole: null,
          adminPermissions: null,
          isLoading: true,
        });
        restoreFromAuthJson('auth.json');
        return false;
      }

      setAuthInitStep('restore_local_storage');
      resetRefreshState();
      const expMs = decodeJwtExpMs(accessToken);
      const now = Date.now();
      const needsRefresh = !!(
        expMs && refreshToken && (expMs <= now || expMs - now < 5 * 60 * 1000)
      );

      setAuthState({
        isAuthenticated: true,
        accessToken,
        refreshToken,
        user,
        adminRole: adminRole || null,
        adminPermissions: adminPermissions || null,
        isLoading: needsRefresh,
      });

      const finishSessionRestore = async (token, refreshStatus) => {
        syncAuthFromLocalStorage();
        const resolved = token || getStoredAuthRecord()?.accessToken || accessToken;
        noteBootAuthProgress();
        markUsableAuthedStateReached();
        setAuthState({ accessToken: resolved, isLoading: false });
        if (resolved) scheduleProactiveRefresh(resolved);
        notifyBatonAuthReady('localStorage-restore');
        void runBackgroundAuthHydrate(resolved, 'localStorage-restore');
        logSessionRestoreOutcome('localStorage', refreshStatus, resolved?.length ?? 0, refreshToken?.length ?? 0);
      };

      if (needsRefresh) {
        setAuthInitStep('refresh_token_startup');
        try { logBootTokenSnapshot('AuthView:initializeAuth:before-refresh'); } catch { /* diagnostic only */ }
        refreshAccessTokenShared('AuthView:initializeAuth')
          .then((result) => {
            if (result.fatalRejected || isSessionDead()) {
              setAuthState({
                isAuthenticated: false,
                accessToken: null,
                refreshToken: null,
                user: null,
                isLoading: false,
                error: null,
                sessionRevoked: true,
              });
              return;
            }
            const token = result.accessToken || accessToken;
            finishSessionRestore(token, result.refreshed ? 'refreshed' : result.alreadyUsedAbsorbed ? 'absorbed' : 'cached');
          })
          .catch(() => {
            if (isSessionDead()) {
              setAuthState({ isLoading: false, sessionRevoked: true });
              return;
            }
            finishSessionRestore(accessToken, 'refresh_failed').catch(() => {
              setAuthState({ isLoading: false });
            });
          });
      } else if (accessToken) {
        finishSessionRestore(accessToken, 'cached').catch(() => {
          setAuthState({ isLoading: false });
        });
      }
      return true;
    }
  } catch (e) {
    console.error('Failed to restore auth state:', e);
  }

  // Tauri fallback: WKWebView may clear localStorage between launches.
  // If auth.json exists on disk (written by the Tauri app or daemon), use
  // its refresh token to restore the session without requiring re-login.
  if (typeof window !== 'undefined' && '__TAURI__' in window) {
    setAuthInitStep('restore_auth_json');
    setAuthState({ isLoading: true, isAuthenticated: false });
    restoreFromAuthJson('auth.json');
    return false;
  }

  setAuthInitStep('unauthenticated');
  setAuthState({ isLoading: false });
  return false;
}

async function restoreFromAuthJson(source = 'auth.json') {
  if (_restoreFromAuthJsonAbort || isSessionDead() || isBootSurrendered()) {
    setAuthState({ isLoading: false, isAuthenticated: false, sessionRevoked: isSessionDead() });
    return;
  }
  setAuthState({ isLoading: true });
  noteBootAuthProgress();
  try {
    setAuthInitStep('read_auth_json_ipc');
    if (_restoreFromAuthJsonAbort) {
      setAuthState({ isLoading: false });
      return;
    }
    try { logBootTokenSnapshot('AuthView:restoreFromAuthJson:before-refresh'); } catch { /* diagnostic only */ }

    setAuthInitStep('refresh_token_http');
    const result = await recoverSessionFromAuthJson(source);

    if (_restoreFromAuthJsonAbort) {
      setAuthState({ isLoading: false });
      return;
    }

    if (result.ok) {
      applyRestoredSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        user: result.user,
        source,
      });
      return;
    }

    logSessionRestoreOutcome(source, result.reason, 0, 0);
    if (result.fatal) {
      const declaw = applyAuthDeclawShadow(
        { trigger: 'auth_family_revoked', reason: result.reason },
        'AuthView:restoreFromAuthJson',
      );
      if (declaw.suppression_applied) {
        console.warn('[Auth] auth.json refresh rejected — streaming restore banner');
        noteBootAuthProgress();
        setAuthState({ isLoading: false, isAuthenticated: true, sessionBootstrapFailed: false });
        return;
      }
      console.warn('[Auth] auth.json refresh rejected — boot terminal sign-in required');
      markRefreshFailed();
      applyBootTerminalSignInRequired();
      return;
    }

    const daemonUnreachable = result.reason === 'invalid_token'
      && typeof window !== 'undefined'
      && '__TAURI__' in window;
    if (daemonUnreachable) {
      try {
        const { isConnectorAvailable } = await import('../lib/connector');
        if (!isConnectorAvailable()) {
          surrenderBootToSignIn({ stillConnecting: false, connectivityNote: true });
          return;
        }
      } catch { /* fall through */ }
    }

    console.warn('[Auth] auth.json refresh failed — surrendering to sign-in (no auto-retry loop)');
    surrenderBootToSignIn({ stillConnecting: false });
  } catch (e) {
    console.error('[Auth] restoreFromAuthJson failed:', e);
    logSessionRestoreOutcome(source, 'exception', 0, 0);
  } finally {
    if (authState.isLoading) {
      setAuthInitStep(authState.isAuthenticated ? 'ready' : 'unauthenticated');
      setAuthState({ isLoading: false });
    }
  }
}

initializeAuth();

/** Retry cold-boot session restore after timeout or transient bootstrap failure. */
export function retryAuthBootstrap() {
  if (isBootSurrendered()) {
    clearBootSurrendered();
  }
  _restoreFromAuthJsonAbort = false;
  clearRefreshBlock();
  resetRefreshState();
  resetSessionDeadState();
  clearTerminalAuthFamilyLatch();
  startAuthInitWatchdog(true);
  setAuthState({
    authInitTimedOut: false,
    sessionBootstrapFailed: false,
    error: null,
    sessionRevoked: false,
    isLoading: true,
  });
  initializeAuth();
}

/** Obtain a valid refresh token for the background daemon (localStorage → auth.json → API refresh). */
export async function resolveRefreshTokenForDaemon() {
  if (shouldBlockAutomaticSessionRestore()) {
    return { refreshToken: null, accessToken: null, userId: '', email: '' };
  }
  syncAuthFromLocalStorage();
  const stored = getStoredAuthRecord();
  let refreshToken = authState.refreshToken || stored?.refreshToken || null;
  const accessToken = authState.accessToken || stored?.accessToken || null;
  const userId = authState.user?.id || stored?.user?.id || '';
  const email = authState.user?.email || stored?.user?.email || '';

  if (isValidRefreshToken(refreshToken)) {
    return { refreshToken, accessToken, userId, email };
  }

  if (typeof window !== 'undefined' && '__TAURI__' in window) {
    try {
      const { readAuthJson } = await import('../lib/connector');
      const creds = await readAuthJson();
      if (isValidRefreshToken(creds?.refresh_token)) {
        setAuthState({
          refreshToken: creds.refresh_token,
          user: authState.user || { id: creds.user_id, email: creds.email },
        });
        return {
          refreshToken: creds.refresh_token,
          accessToken,
          userId: creds.user_id || userId,
          email: creds.email || email,
        };
      }
    } catch {}
  }

  const attempt = refreshToken || stored?.refreshToken;
  if (attempt && attempt.length >= 4 && !isRefreshBlocked()) {
    try {
      const result = await refreshAccessTokenShared('AuthView:resolveRefreshTokenForDaemon');
      if (result.accessToken && isValidRefreshToken(result.refreshToken)) {
        setAuthState({
          isAuthenticated: true,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          user: authState.user || stored?.user,
          isLoading: false,
        });
        return {
          refreshToken: result.refreshToken,
          accessToken: result.accessToken,
          userId,
          email,
        };
      }
    } catch {}
  }

  return { refreshToken: null, accessToken, userId, email };
}

export async function signUp(email, password, metadata) {
  setAuthState({ isLoading: true, error: null });
  
  try {
    const response = await fetch(`${API_URL}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, metadata }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      const err = new Error(data.error || 'Sign up failed');
      err.code = data.code || null;
      throw err;
    }
    
    if (data.session) {
      clearRefreshBlock();
      resetRefreshState();
      resetSessionDeadState();
      resetServiceSessionDeadState();
      clearTerminalAuthFamilyLatch();
      markUsableAuthedStateReached();
      const { accessToken, refreshToken } = extractSessionTokens(data);
      if (refreshToken) {
        try {
          await syncDaemonCredentials(refreshToken, data.user?.id, email, { force: true });
        } catch (credErr) {
          const msg = credErr?.message
            || 'Account created, but background service credentials could not be saved. Quit and reopen Maestro, then sign in again.';
          setAuthState({ isLoading: false, error: msg });
          throw credErr instanceof Error ? credErr : new Error(msg);
        }
      } else {
        console.error('[Auth] signup returned no valid refresh_token — remote library service may not persist');
      }
      setAuthState({
        isAuthenticated: true,
        user: data.user,
        accessToken,
        refreshToken,
        isLoading: false,
        sessionRevoked: false,
      });
      clearExplicitSignOut();
      noteAuthedOnceThisInstall();
    } else {
      setAuthState({ isLoading: false });
      return { needsConfirmation: true, message: data.message };
    }
    
    return { success: true, user: data.user };
  } catch (error) {
    setAuthState({ isLoading: false, error: error.message });
    throw error;
  }
}

export async function signIn(email, password) {
  _restoreFromAuthJsonAbort = true;
  purgeBootTerminalStoredSession();
  clearBootSurrendered();
  clearBootHoldSignIn();
  setAuthState({ isLoading: true, error: null });
  
  try {
    let response;
    try {
      response = await fetch(`${API_URL}/api/auth/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    } catch (networkErr) {
      throw new Error('Unable to reach the server. Please check your internet connection and try again.');
    }

    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error('Server returned an unexpected response. Please try again.');
    }
    
    if (!response.ok) {
      const errMsg = data?.error || 'Sign in failed';
      if (errMsg.toLowerCase().includes('invalid login')) {
        throw new Error('Incorrect email or password. Please try again, or use "Forgot password?" to reset.');
      }
      throw new Error(errMsg);
    }

    if (!data?.session?.access_token && !data?.access_token) {
      throw new Error('Sign in failed — no session returned. Please try again.');
    }

    clearRefreshBlock();
    resetRefreshState();
    resetSessionDeadState();
    resetServiceSessionDeadState();
    clearTerminalAuthFamilyLatch();
    markUsableAuthedStateReached();
    const { accessToken, refreshToken } = extractSessionTokens(data);
    console.log(`[Auth] signIn tokens: access=${accessToken?.length || 0} refresh=${refreshToken?.length || 0}`);

    if (refreshToken) {
      try {
        await syncDaemonCredentials(refreshToken, data.user?.id, email, { force: true });
      } catch (credErr) {
        const msg = credErr?.message
          || 'Signed in to Maestro cloud, but background service credentials could not be saved. Quit and reopen Maestro, then sign in again.';
        setAuthState({ isLoading: false, error: msg });
        throw credErr instanceof Error ? credErr : new Error(msg);
      }
    } else {
      console.error('[Auth] signIn returned no valid refresh_token — sign out and sign in again, or contact support');
      setAuthState({
        isLoading: false,
        error: 'Signed in, but remote library access needs a fresh login. Please sign out and sign in once more.',
      });
      throw new Error('Sign in failed — no valid refresh token for background service');
    }

    setAuthState({
      isAuthenticated: true,
      user: data.user,
      accessToken,
      refreshToken,
      isLoading: false,
      sessionRevoked: false,
      authInitTimedOut: false,
      sessionBootstrapFailed: false,
      bootStillConnecting: false,
      bootConnectivityNote: false,
    });
    clearExplicitSignOut();
    noteAuthedOnceThisInstall();
    if (accessToken) scheduleProactiveRefresh(accessToken);

    setRememberedLoginEmail(email);
    notifyBatonAuthReady('signIn');

    // Fetch admin role in background (non-blocking)
    try {
      const userRes = await fetch(`${API_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (userRes.ok) {
        applyAuthUserPayload(await userRes.json());
      }
    } catch {}
    
    return { success: true, user: data.user };
  } catch (error) {
    setAuthState({ isLoading: false, error: error.message });
    throw error;
  }
}

/** Re-authenticate in place — heals service auth.json without signing out of the web session. */
export async function reauthenticateInPlace(email, password) {
  return signIn(email, password);
}

export async function signOut() {
  _restoreFromAuthJsonAbort = true;
  clearRefreshBlock();
  clearProactiveRefreshTimer();
  markExplicitSignOut();

  const token = authState.accessToken;
  const machineId = localStorage.getItem('maestro_machine_id');

  // Local-first: clear credentials and halt reconnect unconditionally.
  resetRefreshState();
  resetSessionDeadState();
  resetServiceSessionDeadState();
  setAuthState({
    isAuthenticated: false,
    user: null,
    accessToken: null,
    refreshToken: null,
    adminRole: null,
    adminPermissions: null,
    isLoading: false,
    sessionRevoked: false,
  });

  try {
    const { getClientTransport } = await import('../handlers/ClientTransportHandler');
    getClientTransport().disconnect();
  } catch { /* ignore */ }

  const serverRevoke = token
    ? fetch(`${API_URL}/api/auth/signout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
    : Promise.resolve();
  void Promise.race([
    serverRevoke,
    new Promise((resolve) => setTimeout(resolve, 5000)),
  ]).catch(() => {});

  if (machineId && token) {
    void import('../hooks/usePeerNetwork')
      .then(({ unregisterPeer }) => Promise.race([
        unregisterPeer(machineId),
        new Promise((resolve) => setTimeout(resolve, 5000)),
      ]))
      .catch(() => {});
  }
}

async function resetPassword(email) {
  setAuthState({ isLoading: true, error: null });
  
  try {
    const response = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Password reset failed');
    }
    
    setAuthState({ isLoading: false });
    return { success: true };
  } catch (error) {
    setAuthState({ isLoading: false, error: error.message });
    throw error;
  }
}

export function handleEmailConfirmationRedirect() {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;

  const params = new URLSearchParams(hash.substring(1));
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const type = params.get('type');

  if (accessToken && (type === 'signup' || type === 'magiclink' || type === 'email')) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    clearRefreshBlock();

    setAuthState({
      isAuthenticated: true,
      accessToken,
      refreshToken: refreshToken || null,
      user: null,
      isLoading: false,
    });
    clearExplicitSignOut();
    noteAuthedOnceThisInstall();

    return { confirmed: true, type };
  }

  return null;
}

export function useAuth() {
  const [state, setState] = useState(() => authState);

  useEffect(() => {
    registerAuthWindowListeners();
    setState(authState);
    const unsub = subscribeToAuth((newState) => {
      console.log('[Auth] State update:', { isAuth: newState.isAuthenticated, email: newState.user?.email });
      setState(newState);
    });
    return () => {
      unsub();
      disposeAuthWindowListeners();
    };
  }, []);

  return state;
}

// =============================================================================
// AUTHVIEW COMPONENT
// =============================================================================

const GOLD = '#C9A84C';

export default function AuthView({ onAuthenticated, isModal = false }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const iosShell = isIosShell();
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState(() => getRememberedLoginEmail());
  const [savedEmailHint, setSavedEmailHint] = useState(() => getRememberedLoginEmail());
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const errorRef = useRef(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error]);

  const handleResendConfirmation = useCallback(async () => {
    if (!email) return;
    setIsLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/auth/resend-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setError(null);
        setNeedsConfirmation(false);
        setSuccess('Confirmation email sent! Check your inbox (and spam folder), then click the link to confirm your account.');
      } else {
        setError(data?.error || 'Failed to resend confirmation email.');
      }
    } catch {
      setError('Unable to resend. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  useEffect(() => {
    const result = handleEmailConfirmationRedirect();
    if (result?.confirmed) {
      setMode('confirmed');
      const timer = setTimeout(() => {
        onAuthenticated?.();
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setNeedsConfirmation(false);
    setIsLoading(true);
    if (mode !== 'reset') setRememberedLoginEmail(email);
    try {
      if (mode === 'signin') {
        try {
          await signIn(email, password);
          onAuthenticated?.();
        } catch (err) {
          const msg = err.message?.toLowerCase() || '';
          if (msg.includes('email not confirmed') || msg.includes('not confirmed') || msg.includes('confirm your email')) {
            setNeedsConfirmation(true);
            throw new Error('Your email address has not been confirmed yet. Please check your inbox (and spam folder) for the confirmation link, or click the button below to resend it.');
          }
          throw err;
        }
      } else if (mode === 'signup') {
        if (iosShell) {
          throw new Error(t('ios.store.signInOnly'));
        }
        const result = await signUp(email, password);
        if (result?.needsConfirmation) {
          setSuccess(result.message || 'Check your inbox for a confirmation link.');
          setMode('signin');
        } else {
          onAuthenticated?.();
        }
      } else if (mode === 'reset') {
        await resetPassword(email);
        setSuccess('Password reset email sent. Check your inbox.');
        setMode('signin');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    color: '#111',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s',
    boxSizing: 'border-box',
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = GOLD;
    e.target.style.boxShadow = `0 0 0 3px ${GOLD}20`;
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = '#e0e0e0';
    e.target.style.boxShadow = 'none';
  };

  // ---- Email Confirmed Welcome Screen ----
  if (mode === 'confirmed') {
    return (
      <div style={{
        minHeight: isModal ? 'auto' : '100vh',
        background: isModal ? 'transparent' : '#f8f8f8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isModal ? '0' : '20px',
        fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
        <div style={{
          width: '100%',
          maxWidth: '420px',
          background: '#ffffff',
          borderRadius: '24px',
          padding: '48px 40px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
        }}>
          <div style={{
            width: '72px', height: '72px', borderRadius: '20px',
            background: 'transparent', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 24px',
          }}>
            <ValetIcon size={72} className="rounded-[20px]" />
          </div>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'rgba(34, 197, 94, 0.1)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={{
            fontSize: '24px', fontWeight: '700', color: '#111',
            margin: '0 0 8px', letterSpacing: '-0.3px',
          }}>
            Email confirmed!
          </h2>
          <p style={{ color: '#111', fontSize: '16px', fontWeight: '600', margin: '0 0 12px' }}>
            {t('auth.emailConfirmed.headline')}
          </p>
          <p style={{ color: '#666', fontSize: '15px', lineHeight: 1.6, margin: 0 }}>
            {t('auth.emailConfirmed.body')}
          </p>
          <div style={{ marginTop: '24px' }}>
            <div style={{
              width: '32px', height: '32px',
              border: `3px solid ${GOLD}30`,
              borderTopColor: GOLD, borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto',
            }} />
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const showCommunityMessaging = !isModal && !iosShell && (mode === 'signin' || mode === 'signup');

  // ---- Standard Sign In / Reset Form ----
  return (
    <div style={{
      minHeight: isModal ? 'auto' : '100vh',
      background: isModal ? 'transparent' : '#f8f8f8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isModal ? '0' : '20px',
      fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <div className="auth-layout">
        {showCommunityMessaging && (
          <CommunityAuthMessaging
            showPanel
            showMobileHeadline={false}
            showFooter={false}
          />
        )}

        <div className="auth-form-column">
          {showCommunityMessaging && (
            <CommunityAuthMessaging
              showPanel={false}
              showMobileHeadline
              showFooter={false}
            />
          )}

      {/* Auth card */}
      <div style={{
        width: '100%',
        maxWidth: '420px',
        background: '#ffffff',
        borderRadius: '24px',
        padding: isModal ? '32px' : '40px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Logo/branding */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '72px',
            height: '72px',
            borderRadius: '20px',
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <ValetIcon size={72} className="rounded-[20px]" />
          </div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#111',
            margin: 0,
            letterSpacing: '-0.5px',
          }}>
            Maestro
          </h1>
          <p style={{
            color: '#999',
            fontSize: '14px',
            margin: '8px 0 0',
          }}>
            {mode === 'signin' && t('auth.signin.subtitle')}
            {!iosShell && mode === 'signup' && t('auth.signup.subtitle')}
            {mode === 'reset' && 'Reset your password'}
          </p>
          {auth.sessionRevoked && (
            <p style={{
              color: '#666',
              fontSize: '14px',
              margin: '16px 0 0',
              lineHeight: 1.5,
            }}>
              Your session expired — please sign in
            </p>
          )}
          {(auth.bootStillConnecting || auth.authInitTimedOut) && (
            <p
              data-testid="auth-boot-still-connecting"
              style={{
                color: '#666',
                fontSize: 13,
                margin: '12px 0 0',
                lineHeight: 1.5,
              }}
            >
              {t('auth.boot.stillConnecting')}
            </p>
          )}
          {auth.bootConnectivityNote && (
            <p
              data-testid="auth-boot-connectivity-note"
              style={{
                color: '#666',
                fontSize: 13,
                margin: '12px 0 0',
                lineHeight: 1.5,
              }}
            >
              {t('auth.boot.daemonUnreachable')}
            </p>
          )}
          {(auth.authInitTimedOut || auth.sessionBootstrapFailed) && !auth.bootStillConnecting && (
            <div style={{
              marginTop: 16,
              padding: '14px 16px',
              borderRadius: 12,
              background: '#faf8f3',
              border: `1px solid ${GOLD}40`,
              textAlign: 'left',
            }}>
              <p style={{
                color: '#333',
                fontSize: 14,
                fontWeight: 600,
                margin: '0 0 8px',
                lineHeight: 1.45,
              }}>
                {t('auth.sessionBootstrapFailed')}
              </p>
              <p style={{
                color: '#666',
                fontSize: 13,
                margin: '0 0 12px',
                lineHeight: 1.5,
              }}>
                {t('auth.sessionReachabilityNotice')}
              </p>
              <button
                type="button"
                data-testid="auth-bootstrap-retry"
                onClick={() => retryAuthBootstrap()}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: `1px solid ${GOLD}`,
                  background: `${GOLD}18`,
                  color: '#1a1a2e',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {t('auth.sessionBootstrapRetry')}
              </button>
            </div>
          )}
        </div>
        
        {/* Error/Success messages */}
        {error && (
          <div ref={errorRef} style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ fontSize: '18px', flexShrink: 0, lineHeight: 1.3 }}>⚠️</span>
              <span style={{ color: '#dc2626', fontSize: '14px', lineHeight: 1.4 }}>{error}</span>
            </div>
            {needsConfirmation && (
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={isLoading}
                style={{
                  marginTop: '12px',
                  width: '100%',
                  padding: '10px',
                  background: GOLD,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#1a1a2e',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                }}
              >
                Resend Confirmation Email
              </button>
            )}
          </div>
        )}
        
        {success && (
          <div style={{
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}>
            <span style={{ fontSize: '18px' }}>✅</span>
            <span style={{ color: '#16a34a', fontSize: '14px' }}>{success}</span>
          </div>
        )}
        
        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: '#555',
              fontSize: '13px',
              fontWeight: '500',
              marginBottom: '8px',
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              style={inputStyle}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
            {mode === 'signin' && savedEmailHint && email === savedEmailHint && (
              <button
                type="button"
                onClick={() => {
                  clearRememberedLoginEmail();
                  setSavedEmailHint('');
                  setEmail('');
                }}
                style={{
                  marginTop: 8,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#888',
                  fontSize: 12,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                }}
              >
                Not you?
              </button>
            )}
          </div>
          
          {(mode === 'signin' || (!iosShell && mode === 'signup')) && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#555',
                fontSize: '13px',
                fontWeight: '500',
                marginBottom: '8px',
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'Choose a password' : '••••••••'}
                required
                minLength={mode === 'signup' ? 6 : undefined}
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
          )}
          
          {mode === 'signin' && (
            <div style={{ marginBottom: '20px', textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => { setMode('reset'); setError(null); setSuccess(null); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: GOLD,
                  fontSize: '13px',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Forgot password?
              </button>
            </div>
          )}
          
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '14px',
              background: isLoading
                ? `${GOLD}80`
                : `linear-gradient(135deg, ${GOLD}, #E8C94A, ${GOLD})`,
              border: 'none',
              borderRadius: '12px',
              color: '#1a1a2e',
              fontSize: '15px',
              fontWeight: '700',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: isLoading ? 'none' : `0 4px 16px ${GOLD}40`,
            }}
          >
            {isLoading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(26, 26, 46, 0.3)',
                  borderTopColor: '#1a1a2e',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
                Loading...
              </span>
            ) : (
              <>
                {mode === 'signin' && 'Sign In'}
                {!iosShell && mode === 'signup' && 'Create Account & Start Trial'}
                {mode === 'reset' && 'Send Reset Link'}
              </>
            )}
          </button>
        </form>
        
        {/* Mode toggle */}
        <div style={{
          marginTop: '24px',
          textAlign: 'center',
          color: '#999',
          fontSize: '14px',
        }}>
          {mode === 'signin' && !iosShell && (
            <>
              Don&rsquo;t have an account?{' '}
              <button
                onClick={() => { setMode('signup'); setError(null); setSuccess(null); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: GOLD,
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Start Your Free 30-Day Trial &rarr;
              </button>
            </>
          )}
          {!iosShell && mode === 'signup' && (
            <>
              Already have an account?{' '}
              <button
                onClick={() => { setMode('signin'); setError(null); setSuccess(null); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: GOLD,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Sign in
              </button>
            </>
          )}
          {mode === 'reset' && (
            <>
              Remember your password?{' '}
              <button
                onClick={() => { setMode('signin'); setError(null); setSuccess(null); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: GOLD,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </div>

          {showCommunityMessaging && (
            <CommunityAuthMessaging
              showPanel={false}
              showMobileHeadline={false}
              showFooter
            />
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        ${communityLayoutStyles('auth')}
      `}</style>
    </div>
  );
}
